import React from 'react';
import {
  IGroup,
  IRouteScreenProps,
  ISupplierUserProfile,
  ICreateGroupResponse,
} from 'constants/interfaces';
import {apiAdminGetGroups} from 'constants/Api';
import {
  Box,
  Button,
  ButtonGroup,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {Route} from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import NavigationService from 'services/NavigationService';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  groupTableFilters,
  groupTableHeaders,
  groupTableRows,
} from 'screens/Dashboard/Groups/GroupTableSetup';
import {connect} from 'react-redux';
import moment from 'moment';
import {PermissionsService} from 'services/PermissionsService';
import {CreateGroupDialog} from './CreateGroupDialog';

import './Groups.scss';

type GroupsProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
};

type GroupsState = {
  isLoading: boolean;
  isRefreshing: boolean;
  showRefreshedSnackbar: boolean;
  fetchSize?: number;
  groups: IGroup[];
  lastUpdate?: Date;
  showCreateDialog: boolean;
};

const OPTIMISTIC_GROUP_CACHE_KEY = 'khulaSupplier.optimisticGroup';

class Groups extends React.Component<GroupsProps, GroupsState> {
  constructor(props: GroupsProps) {
    super(props);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
  }

  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    fetchSize?: number;
    groups: IGroup[];
    lastUpdate?: Date;
    showCreateDialog: boolean;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    fetchSize: 20,
    groups: [],
    lastUpdate: undefined,
    showCreateDialog: false,
  };

  getPersistedOptimisticGroup(): IGroup | null {
    try {
      const stored = window.localStorage.getItem(OPTIMISTIC_GROUP_CACHE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      return null;
    }
  }

  persistOptimisticGroup(group: IGroup) {
    try {
      window.localStorage.setItem(
        OPTIMISTIC_GROUP_CACHE_KEY,
        JSON.stringify(group),
      );
    } catch (err) {
      // ignore localStorage failures
    }
  }

  clearPersistedOptimisticGroup() {
    try {
      window.localStorage.removeItem(OPTIMISTIC_GROUP_CACHE_KEY);
    } catch (err) {
      // ignore localStorage failures
    }
  }

  componentDidMount() {
    this.loadGroups();
  }

  loadGroups() {
    const {groups, lastUpdate} = this.state;
    const groupsAreExpired =
      !lastUpdate ||
      moment(lastUpdate).isBefore(moment().subtract(30, 'minutes'));
    const noGroups = !groups?.length;

    if (groupsAreExpired || noGroups) {
      this.refreshGroups();
    } else {
      this.setState({isLoading: false});
    }
  }

  refreshGroups() {
    this.setState({isRefreshing: true}, () => {
      apiAdminGetGroups()
        .then((groups: IGroup[]) => {
          const persistedGroup = this.getPersistedOptimisticGroup();
          const mergedGroups =
            persistedGroup &&
            !groups.some((g) => g.groupId === persistedGroup.groupId)
              ? [persistedGroup, ...groups]
              : groups;

          if (
            persistedGroup &&
            groups.some((g) => g.groupId === persistedGroup.groupId)
          ) {
            this.clearPersistedOptimisticGroup();
          }

          this.setState({
            groups: mergedGroups,
            lastUpdate: new Date(),
            showRefreshedSnackbar: true,
          });
        })
        .catch((err) => {
          alert('Failed to load groups. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false, isRefreshing: false}));
    });
  }

  handleFetchSizeUpdate(size?: number) {
    this.setState({fetchSize: size});
  }

  handleRowsPerPageChange(size: number) {
    const normalizedSize = size === -1 ? undefined : size;
    this.handleFetchSizeUpdate(normalizedSize);
  }

  handleGroupCreated(created: ICreateGroupResponse) {
    // Build a partial IGroup from the mutation response so it appears
    // immediately in the list (groups may not return it right away).
    const optimisticGroup: IGroup = {
      groupId: created.groupId,
      name: created.name,
      description: created.description,
      isPrivate: created.isPrivate,
      isArchived: false,
      createdAt: new Date().toISOString(),
      memberCount: 1,
      visibility: created.isPrivate ? 'Private' : 'Public',
      owner: created.createdBy
        ? {
            userId: created.createdBy.userId,
            username: created.createdBy.username,
          }
        : undefined,
      admins: created.admins ?? [],
    };

    // Prepend optimistically — do NOT call refreshGroups() here because the
    // server's groups query won't return the new group immediately,
    // which would overwrite and erase the optimistic entry.
    this.persistOptimisticGroup(optimisticGroup);
    this.setState((prev) => ({
      showCreateDialog: false,
      groups: [optimisticGroup, ...prev.groups],
    }));
  }

  render() {
    const {
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      fetchSize,
      groups,
      lastUpdate,
      showCreateDialog,
    } = this.state;

    const permissions = new PermissionsService(this.props.user);
    const canWrite = permissions.shouldShowGroupsWrite();

    const toolbarActions = (
      <>
        <ButtonGroup variant="text">
          {[20, 50, undefined].map((size, index) => (
            <Button
              key={index}
              variant={fetchSize === size ? 'contained' : 'text'}
              disabled={isLoading || isRefreshing}
              disableElevation
              onClick={() => this.handleFetchSizeUpdate(size)}
              size={'small'}
            >
              {size || 'All'}
            </Button>
          ))}
        </ButtonGroup>
        {canWrite && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            className="teams-btn"
            color={'primary'}
            onClick={() => this.setState({showCreateDialog: true})}
          >
            New Group
          </Button>
        )}
      </>
    );

    return (
      <div className="table-screen groups-container">
        <Typography variant="h4" className="page-title">
          Groups
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          Manage your farmer groups
        </Typography>
        <Route
          render={() => (
            <div className="groups-section">
              <GroupsTable
                groups={groups}
                isLoading={isLoading}
                isRefreshing={isRefreshing}
                onRefresh={() => this.refreshGroups()}
                fetchSize={fetchSize}
                onRowsPerPageChange={this.handleRowsPerPageChange}
                toolbarActions={toolbarActions}
              />
              <Box mt={1}>
                <Typography variant={'caption'}>
                  {lastUpdate
                    ? `Last updated: ${moment(lastUpdate).fromNow()}`
                    : ''}
                </Typography>
              </Box>
              <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                open={showRefreshedSnackbar}
                onClose={() => this.setState({showRefreshedSnackbar: false})}
                autoHideDuration={3000}
                message="Groups refreshed"
              />
            </div>
          )}
        />
        <CreateGroupDialog
          open={showCreateDialog}
          onClose={() => this.setState({showCreateDialog: false})}
          onCreated={(created) => this.handleGroupCreated(created)}
        />
      </div>
    );
  }
}

const GroupsTable = (props: {
  groups: IGroup[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: VoidFunction;
  fetchSize?: number;
  onRowsPerPageChange?: (size: number) => void;
  toolbarActions?: React.ReactNode;
}) => {
  const {
    groups,
    isLoading,
    isRefreshing,
    onRefresh,
    fetchSize,
    onRowsPerPageChange,
    toolbarActions,
  } = props;

  const mapRowToGroup = (row: Row) => groups.find((g) => g.groupId === row.id);

  return (
    <KhulaSupplierTable
      headers={groupTableHeaders()}
      rows={groupTableRows(groups, (group) => {
        NavigationService.navigate(`/groups/${group.groupId}`, {group});
      })}
      filtersGroups={groupTableFilters}
      defaultActiveFilters={[]}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      defaultRowsPerPage={typeof fetchSize === 'number' ? fetchSize : -1}
      rowsPerPageOptions={[20, 50, {label: 'All', value: -1}]}
      onRowsPerPageChange={onRowsPerPageChange}
      toolbarActions={toolbarActions}
      onRowSelect={(row) => {
        const group = mapRowToGroup(row);
        if (!group) return;
        NavigationService.navigate(`/groups/${group.groupId}`, {group});
      }}
    />
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
});

export default connect(mapStateToProps)(Groups);
